import React, { forwardRef, useState } from "react";
import styles from "./AuthLayout.module.css";

// A labelled input for the auth surface: icon, optional password reveal, and
// an error slot underneath.
//
// forwardRef so react-hook-form's register() can be spread straight onto it —
// `{...register("username", { required: true })}` passes name/onChange/onBlur
// as props and `ref` through the forwarded ref.
//
// Focus styling lives in CSS (:focus-within on the wrapper) rather than in
// framer-motion: it's cheaper, and it doesn't fight the browser's own focus
// painting on a control the user is typing into.
const AuthField = forwardRef(
  (
    {
      id,
      label,
      icon,
      error,
      hint,
      type = "text",
      canReveal = false,
      revealLabel,
      hideRevealLabel,
      ...inputProps
    },
    ref
  ) => {
    const [revealed, setRevealed] = useState(false);
    const toggleLabel = revealed ? hideRevealLabel : revealLabel;

    return (
      <div className={styles.field}>
        <label className={styles.label} htmlFor={id}>
          {label}
        </label>

        <div
          className={`${styles.inputWrap} ${error ? styles.invalid : ""} ${
            inputProps.disabled ? styles.disabledWrap : ""
          }`}
        >
          {icon && <span className={`${icon} ${styles.icon}`} aria-hidden="true" />}

          <input
            id={id}
            ref={ref}
            type={canReveal && revealed ? "text" : type}
            className={styles.input}
            aria-invalid={error ? "true" : undefined}
            aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
            {...inputProps}
          />

          {canReveal && (
            <button
              type="button"
              className={styles.reveal}
              onClick={() => setRevealed((r) => !r)}
              aria-label={toggleLabel}
              title={toggleLabel}
            >
              <span className={revealed ? "fas fa-eye-slash" : "fas fa-eye"} aria-hidden="true" />
            </button>
          )}
        </div>

        {/* The error replaces the hint rather than stacking with it — two
            lines of guidance under one field is noise. */}
        {error ? (
          <div className={styles.error} id={`${id}-error`}>
            {error}
          </div>
        ) : (
          hint && (
            <div className={styles.hint} id={`${id}-hint`}>
              {hint}
            </div>
          )
        )}
      </div>
    );
  }
);

AuthField.displayName = "AuthField";

export default AuthField;
